import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import { MdOutlineMenuOpen } from 'react-icons/md';
import { applySessionProfileFromApi } from '../../../profileIdentity/sessionProfile';
import { buildProfilePictureUrlForMode } from '../../../MainHeader/profilePictureUrl';
import { useDiscussionViewLater } from './useDiscussionViewLater';
import { usePostsShellNavCollapsed, isPostsShellNavLayoutReady } from './usePostsShellNavCollapsed';
import { usePostsShellWheelScroll } from './usePostsShellWheelScroll';
import { loadStarterClubCatalog } from '../config/starterClubCatalog';
import { defaultHomeFeedSort } from '../lib/feedPersonalization';
import { isNotificationsDropdownPortalTarget } from '../../../notifications/notificationUtils';
import { isMessagingDropdownPortalTarget } from '../../../MainHeader/Messaging/messagingUtils';
import { useMessagingUnreadCount } from '../../../MainHeader/Messaging/useMessagingUnreadCount';
import { buildSearchUrl } from '../../../search/searchRouting';
import { POSTS_SHELL_DRAWER_MQ } from '../lib/feedViewMode';
import { appApiUrl, resolveAppApiOrigin } from '../../../../utils/appApiOrigin';
import { markSessionValid } from '../../../../utils/sessionAuth';

const configuredBaseUrl = process.env.REACT_APP_BASE_URL || '';
const apiBaseUrl = () => resolveAppApiOrigin() || configuredBaseUrl;

const SHELL_HEADER_DROPDOWNS_CLOSED = { messaging: false, notifications: false, profile: false };

/**
 * Shared posts shell state: profile, theme, header dropdowns, mobile drawer, nav collapse.
 */
export function usePostsShellLayout({
  returnTo,
  addNotification,
  appShell = false,
  enableWheelScroll = !appShell,
} = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedReturnTo = returnTo ?? `${location.pathname}${location.search}`;

  const [profile, setProfile] = useState(null);
  const [profileResolved, setProfileResolved] = useState(false);
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem('theme') || 'light');
  const [headerDropdowns, setHeaderDropdowns] = useState({
    messaging: false,
    notifications: false,
    profile: false,
  });
  const [isMobileLayout, setIsMobileLayout] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(POSTS_SHELL_DRAWER_MQ).matches;
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [feedSearchQ, setFeedSearchQ] = useState('');

  const messagingRef = useRef(null);
  const notificationsRef = useRef(null);
  const profileRef = useRef(null);
  const shellRootRef = useRef(null);

  const { isNavCollapsed, toggleNavCollapsed } = usePostsShellNavCollapsed(
    profile?.userId,
    profileResolved,
    profile?.tenantId
  );
  const viewLater = useDiscussionViewLater(profile, addNotification);
  const { unreadCount: messagesUnreadCount, hasUnread: hasUnreadMessages } =
    useMessagingUnreadCount(apiBaseUrl());
  usePostsShellWheelScroll(shellRootRef, enableWheelScroll && !isMobileLayout);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(appApiUrl('/api/profile'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.userProfile) {
        markSessionValid();
        setProfile(applySessionProfileFromApi(data));
      } else {
        setProfile(null);
      }
    } catch {
      setProfile(null);
    } finally {
      setProfileResolved(true);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeMode);
    localStorage.setItem('theme', themeMode);
  }, [themeMode]);

  useEffect(() => {
    loadStarterClubCatalog();
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    const onSessionEnded = () => {
      setProfile(null);
      setProfileResolved(true);
    };
    window.addEventListener('bahlus-session-ended', onSessionEnded);
    return () => window.removeEventListener('bahlus-session-ended', onSessionEnded);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia(POSTS_SHELL_DRAWER_MQ);
    const sync = () => setIsMobileLayout(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!isMobileLayout) setMobileDrawerOpen(false);
  }, [isMobileLayout]);

  useEffect(() => {
    if (!isMobileLayout) {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      return undefined;
    }
    if (!mobileDrawerOpen) return undefined;

    if (appShell) {
      const preventBackgroundScroll = (e) => {
        if (e.target.closest('.support-open-disc-shell-sidebar-col')) return;
        if (e.target.closest('.support-open-disc-shell-mobile-chrome')) return;
        e.preventDefault();
      };
      document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
      return () => document.removeEventListener('touchmove', preventBackgroundScroll);
    }

    const preventBackgroundScroll = (e) => {
      if (e.target.closest('.support-open-disc-shell-sidebar-col')) return;
      if (e.target.closest('.support-open-disc-shell-mobile-chrome')) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });
    return () => document.removeEventListener('touchmove', preventBackgroundScroll);
  }, [isMobileLayout, mobileDrawerOpen, appShell]);

  useEffect(() => {
    if (!isMobileLayout) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || !mobileDrawerOpen) return;
      setMobileDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileLayout, mobileDrawerOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        messagingRef.current &&
        !messagingRef.current.contains(event.target) &&
        !isMessagingDropdownPortalTarget(event.target)
      ) {
        setHeaderDropdowns((prev) => ({ ...prev, messaging: false }));
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target) &&
        !isNotificationsDropdownPortalTarget(event.target) &&
        !isMessagingDropdownPortalTarget(event.target)
      ) {
        setHeaderDropdowns((prev) => ({ ...prev, notifications: false }));
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setHeaderDropdowns((prev) => ({ ...prev, profile: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const profileAvatarSrc = useMemo(
    () =>
      buildProfilePictureUrlForMode(profile?.profilePictureUrl, apiBaseUrl(), profile?.profileMode, {
        userId: profile?.userId,
        name: profile?.displayName || profile?.name,
      }),
    [profile?.profilePictureUrl, profile?.profileMode, profile?.userId, profile?.displayName, profile?.name]
  );

  const isGuestViewer = profileResolved && !profile?.userId;

  const toggleHeaderDropdown = useCallback((key) => {
    setHeaderDropdowns((prev) => {
      const opening = !prev[key];
      if (opening && isMobileLayout) {
        setMobileDrawerOpen(false);
      }
      if (opening) {
        return { ...SHELL_HEADER_DROPDOWNS_CLOSED, [key]: true };
      }
      return { ...prev, [key]: false };
    });
  }, [isMobileLayout]);

  useEffect(() => {
    setHeaderDropdowns(SHELL_HEADER_DROPDOWNS_CLOSED);
  }, [location.pathname]);

  const goPosts = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      p.set('view', 'discussions');
      Object.entries(extra).forEach(([k, v]) => {
        if (v != null && v !== '') p.set(k, v);
      });
      navigate(`/posts?${p.toString()}`);
    },
    [navigate]
  );

  const closeDrawer = useCallback(() => {
    if (isMobileLayout) setMobileDrawerOpen(false);
  }, [isMobileLayout]);

  const toggleShellNav = useCallback(() => {
    if (isMobileLayout) {
      setMobileDrawerOpen((wasOpen) => {
        if (!wasOpen) {
          setHeaderDropdowns(SHELL_HEADER_DROPDOWNS_CLOSED);
        }
        return !wasOpen;
      });
    } else {
      toggleNavCollapsed();
    }
  }, [isMobileLayout, toggleNavCollapsed]);

  const shellNavLayoutReady = isPostsShellNavLayoutReady();
  const shellNavLabel = isMobileLayout
    ? mobileDrawerOpen
      ? 'Close menu'
      : 'Open menu'
    : !shellNavLayoutReady
      ? 'Loading navigation'
      : isNavCollapsed
        ? 'Expand sidebar'
        : 'Collapse sidebar';

  const shellNavIcon =
    isMobileLayout || !shellNavLayoutReady ? (
      <FiMenu className="support-open-disc-shell-menu-icon" aria-hidden />
    ) : isNavCollapsed ? (
      <FiMenu className="support-open-disc-shell-menu-icon" aria-hidden />
    ) : (
      <MdOutlineMenuOpen className="support-open-disc-shell-menu-icon" aria-hidden />
    );

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key !== 'Enter') return;
      const q = String(e.target?.value || '').trim();
      e.preventDefault();
      setFeedSearchQ('');
      navigate(buildSearchUrl({ q }));
    },
    [navigate, setFeedSearchQ]
  );

  const defaultSidebarProps = useMemo(
    () => ({
      profile,
      profileResolved,
      isNavCollapsed,
      drawerOpenClass:
        isMobileLayout && mobileDrawerOpen ? ' support-open-disc-shell-sidebar-col--drawer-open' : '',
      ariaHidden: isMobileLayout && !mobileDrawerOpen ? true : undefined,
      activeHomeSort: defaultHomeFeedSort(),
      suppressNavActive: true,
      onHome: () => {
        goPosts({ sort: defaultHomeFeedSort() });
        closeDrawer();
      },
      onPopular: () => {
        goPosts({ sort: 'votes' });
        closeDrawer();
      },
      onRecommendations: () => {
        goPosts({ sort: 'latest' });
        closeDrawer();
      },
      onClubs: () => {
        navigate('/posts?view=discussions-clubs');
        closeDrawer();
      },
      onMessages: () => {
        navigate('/sm/all-messages');
        closeDrawer();
      },
      isMessagesActive: false,
      hasUnreadMessages,
      messagesUnreadCount,
      onCreatePost: () => {
        goPosts({ compose: '1' });
        closeDrawer();
      },
      onViewLaterSection: () => {
        goPosts({ feed: 'view-later' });
        closeDrawer();
      },
      onMyPostsSection: () => {
        goPosts({ feed: 'my-posts' });
        closeDrawer();
      },
      viewLaterItems: viewLater.viewLaterItems || viewLater.items || [],
    }),
    [
      profile,
      profileResolved,
      isNavCollapsed,
      isMobileLayout,
      mobileDrawerOpen,
      goPosts,
      navigate,
      closeDrawer,
      viewLater.viewLaterItems,
      viewLater.items,
      hasUnreadMessages,
      messagesUnreadCount,
    ]
  );

  return {
    baseUrl: apiBaseUrl(),
    profile,
    profileResolved,
    profileAvatarSrc,
    isGuestViewer,
    themeMode,
    setThemeMode,
    headerDropdowns,
    setHeaderDropdowns,
    toggleHeaderDropdown,
    isMobileLayout,
    mobileDrawerOpen,
    setMobileDrawerOpen,
    isNavCollapsed,
    feedSearchQ,
    setFeedSearchQ,
    messagingRef,
    notificationsRef,
    profileRef,
    shellRootRef,
    resolvedReturnTo,
    shellNavLayoutReady,
    shellNavLabel,
    shellNavIcon,
    toggleShellNav,
    closeDrawer,
    handleSearchKeyDown,
    defaultSidebarProps,
    goPosts,
    navigate,
  };
}
